import React from "react";

// Tiny, dependency-free, XSS-safe markdown renderer for short prose (e.g. bios).
// Supports: paragraphs (blank line), single line breaks, **bold**, *italic* and
// [text](url) links. Everything is emitted as React elements — which React
// escapes by default — and link hrefs are restricted to http(s)/mailto, so no
// untrusted HTML can ever reach the DOM. We deliberately avoid a heavyweight
// markdown dependency (and its CRA/Jest ESM headaches) for this small need.

function safeHref(url: string): string | null {
  const u = url.trim();
  if (/^https?:\/\//i.test(u) || /^mailto:/i.test(u)) return u;
  return null;
}

const INLINE = /\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(([^)\s]+)\)/g;

function renderInline(text: string, keyBase: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  let m: RegExpExecArray | null;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      nodes.push(<strong key={`${keyBase}-b${i}`}>{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      nodes.push(<em key={`${keyBase}-i${i}`}>{m[2]}</em>);
    } else if (m[3] !== undefined) {
      const href = safeHref(m[4]);
      nodes.push(
        href ? (
          <a
            key={`${keyBase}-a${i}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {m[3]}
          </a>
        ) : (
          // Unsafe/relative URL — render the link text as plain text.
          m[3]
        )
      );
    }
    last = INLINE.lastIndex;
    i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function renderMarkdown(text?: string | null): React.ReactNode {
  if (!text) return null;
  const paragraphs = text.replace(/\r\n/g, "\n").split(/\n{2,}/);
  return paragraphs.map((para, pi) => {
    const lines = para.split("\n");
    return (
      <p key={`p${pi}`}>
        {lines.map((line, li) => (
          <React.Fragment key={`p${pi}-l${li}`}>
            {li > 0 && <br />}
            {renderInline(line, `p${pi}-l${li}`)}
          </React.Fragment>
        ))}
      </p>
    );
  });
}
